import { ScheduledTask } from "@/data/models/Task";
import {
  PlanningControlSettings,
  ObjectiveFunctionWeights,
  ObjectiveFunctionWeightNames,
  getEarliestLunchBreakForDate,
} from "@/data/models/SchedulingSettings";
import {
  getDayId,
  TimeSlotWithScheduledTask,
  getTimeslotStartTimestampIncludingTravelTimeAndBreaks,
  getTimeslotEndTimestampIncludingTravelTimeAndBreaks,
  fillTravelTimesAndPauseDurations,
  createTimeSlotForSchedueldTask,
  getWorkingTimeForTimeSlotInMs,
} from "./TimeSlot";
import {
  getDateFromHoursAndMinuteString,
  getDateFromYearMonthDateString,
} from "../DateHelper";
import { SchedulingModule } from "@/store/modules/SchedulingModule";
import { DateTime } from "luxon";

import { getTravelTimeToDepotInMs } from "@/utils/scheduling/TravelTimeProvider";

export interface SchedulingScore {
  taskId: string;
  total: number;
  parts: {
    travelTime: number;
    targetDate: number;
    targetTime: number;
    timeGap: number;
    targetUser: number;
    workingHours: number;
    userRequirements: number;
  };
}

export function getSchedulingScoreParts(schedulingScore: SchedulingScore) {
  const result: string[] = [];

  const helper: (value: number, label: string) => void = (value, label) => {
    if (value != 0) {
      result.push(`${label}: ${value}`);
    }
  };

  result.push(`Gesamt: ${schedulingScore.total}`);

  helper(schedulingScore.parts.travelTime, "Fahrzeit");
  helper(schedulingScore.parts.targetDate, "Datum");
  helper(schedulingScore.parts.targetTime, "Zeit");
  helper(schedulingScore.parts.timeGap, "Zeitlücke");
  helper(schedulingScore.parts.targetUser, "Ausführung durch");
  helper(schedulingScore.parts.workingHours, "Arbeitszeit");

  return result;
}

function calcuateTargetDatePart(scheduledTask: ScheduledTask) {
  const targetDate = getDateFromYearMonthDateString(
    scheduledTask.planningParameters.targetDate
  );

  if (!targetDate) {
    return 0;
  }

  const fromDateTime = DateTime.fromJSDate(scheduledTask.startDate);
  const toDateTime = DateTime.fromJSDate(targetDate);

  const diffInDays = Math.abs(
    Math.floor(fromDateTime.diff(toDateTime, "days").toObject().days ?? 0)
  );

  return diffInDays * 60;
}

function calculateTargetTimePart(
  scheduledTask: ScheduledTask,
  planningControlSettings: PlanningControlSettings
) {
  const targetTime = getDateFromHoursAndMinuteString(
    scheduledTask.planningParameters.targetTime
  );

  if (!targetTime) {
    return 0;
  }

  const startDate = scheduledTask.startDate;
  const startTime = startDate.getMinutes() + startDate.getHours() * 60;

  const acceptableTimeStart =
    targetTime.getMinutes() + targetTime.getHours() * 60;

  if (startTime < acceptableTimeStart) {
    const gapInMinutes = acceptableTimeStart - startTime;
    return Math.round(gapInMinutes);
  }

  const acceptableTimeEnd =
    acceptableTimeStart + planningControlSettings.acceptableDelayInMin;

  if (startTime > acceptableTimeEnd) {
    const gapInMinutes = startTime - acceptableTimeEnd;
    return Math.round(gapInMinutes);
  }

  return 0;
}

function calculateTargetUserPart(scheduledTask: ScheduledTask): number {
  return scheduledTask.planningParameters.targetUserId &&
    scheduledTask.planningParameters.targetUserId !== scheduledTask.userId
    ? 60
    : 0;
}

function calculateTimeGapPart(sortedTimeSlots: TimeSlotWithScheduledTask[]) {
  if (sortedTimeSlots.length < 2) {
    return 0;
  }

  let gapSizeInMinutes = 0;

  const firstTimeSlot = sortedTimeSlots[0];

  let currentEnd = getTimeslotEndTimestampIncludingTravelTimeAndBreaks(
    firstTimeSlot,
    true,
    true
  );

  let isAtDepot = firstTimeSlot?.didTravelToDepot ?? false;

  for (let i = 1; i < sortedTimeSlots.length; i++) {
    const timeSlot = sortedTimeSlots[i];

    if (!isAtDepot) {
      const startOfNextTimeSlot = getTimeslotStartTimestampIncludingTravelTimeAndBreaks(
        timeSlot,
        true,
        true
      );

      gapSizeInMinutes += Math.round(
        (startOfNextTimeSlot - currentEnd) / 60000
      );
    }

    currentEnd = getTimeslotEndTimestampIncludingTravelTimeAndBreaks(
      timeSlot,
      true,
      true
    );

    isAtDepot = timeSlot?.didTravelToDepot ?? false;
  }

  return gapSizeInMinutes;
}

function convertWeightsToPercentage(
  weights: ObjectiveFunctionWeights
): ObjectiveFunctionWeights {
  const result = {
    ...weights,
  };

  ObjectiveFunctionWeightNames.forEach((weightName) => {
    result[weightName] = result[weightName] / 100;
  });

  return result;
}

function calculateObjectiveFunctionForDay(
  scheduledTask: ScheduledTask,
  planningControlSettings: PlanningControlSettings,
  travelTimePart: number,
  timeGapPart: number,
  workingHoursPart: number
): SchedulingScore {
  const targetDatePart = calcuateTargetDatePart(scheduledTask);
  const targetTimePart = calculateTargetTimePart(
    scheduledTask,
    planningControlSettings
  );

  const targetUserPart = calculateTargetUserPart(scheduledTask);

  const percentages = convertWeightsToPercentage(
    planningControlSettings.objectiveFunctionParameters.weights
  );

  const {
    total: totalPercentage,
    travelTime: travelTimePercentage,
    timeGap: timeGapPercentage,
    targetUser: targetUserPercentage,
    dailyWorkTimeExceedance: dailyWorkTimeExceedancePercentage,
  } = percentages;

  const targetDatePercentage = scheduledTask.planningParameters
    .isTargetDateFixed
    ? percentages.targetDateFixed
    : percentages.targetDate;

  const targeTimePercentage = scheduledTask.planningParameters.isTargetTimeFixed
    ? percentages.targetTimeFixed
    : percentages.targetTime;

  const weightedTravelTime = travelTimePart * travelTimePercentage;
  const weightedTimeGap = timeGapPart * timeGapPercentage;

  const weightedTargetDate = targetDatePart * targetDatePercentage;
  const weightedTargetTime = targetTimePart * targeTimePercentage;
  const weightedTargetUser = targetUserPart * targetUserPercentage;
  const weightedWorkingHours =
    workingHoursPart * dailyWorkTimeExceedancePercentage;

  const sum =
    weightedTravelTime +
    weightedTargetDate +
    weightedTargetTime +
    weightedTargetUser +
    weightedTimeGap +
    weightedWorkingHours;

  return {
    taskId: scheduledTask.id,
    total: totalPercentage * sum,
    parts: {
      travelTime: weightedTravelTime,
      targetDate: weightedTargetDate,
      targetTime: weightedTargetTime,
      timeGap: weightedTimeGap,
      targetUser: weightedTargetUser,
      workingHours: weightedWorkingHours,
      userRequirements: 0,
    },
  };
}

function calculateWorkingHoursPart(
  sortedTimeSlots: TimeSlotWithScheduledTask[],
  maxWorkingHoursPerDay: number
) {
  let sum = 0;

  sortedTimeSlots.forEach((timeSlot) => {
    sum += getWorkingTimeForTimeSlotInMs(timeSlot);
  });

  const workingTimeInMin = Math.round(sum / 60000);

  return Math.max(0, workingTimeInMin - maxWorkingHoursPerDay * 60);
}

function calculateTravelTimeSumInMs(
  sortedTimeSlots: TimeSlotWithScheduledTask[]
) {
  let sum = 0;

  sortedTimeSlots.forEach((t) => {
    sum += t.travelTimeToTaskInMs ?? 0;
    sum += t.travelTimeFromTaskInMs ?? 0;
  });

  return sum;
}

export function calculateObjectiveFunctionRelativeToFocusedTask(
  forcedScheduledTask?: ScheduledTask
) {
  const focusedTask = SchedulingModule.focusedTask;

  if (!focusedTask) {
    return undefined;
  }

  const taskId = focusedTask.id;
  const tasksOfAllUsersLookup = SchedulingModule.tasksOfAllUsersLookup;

  const scheduledTask =
    forcedScheduledTask ?? tasksOfAllUsersLookup.getScheduledTask(taskId);

  if (!scheduledTask) {
    return undefined;
  }

  const {
    planningControlSettings,
    workingHoursSettings,
  } = SchedulingModule.schedulingSettings;

  const earliestLunchOnDayOfScheduledTask = getEarliestLunchBreakForDate(
    scheduledTask.startDate,
    workingHoursSettings
  );

  let containsScheduledTask = false;

  let timeSlotsOfScheduledTask = tasksOfAllUsersLookup
    .getAllTimeSlotsForSameUserAndDay(scheduledTask)
    .map((timeSlotWithScheduledTask) => {
      if (timeSlotWithScheduledTask.scheduledTask.id == taskId) {
        containsScheduledTask = true;
        return createTimeSlotForSchedueldTask(scheduledTask);
      } else {
        return { ...timeSlotWithScheduledTask };
      }
    });

  if (!containsScheduledTask) {
    timeSlotsOfScheduledTask = [
      ...timeSlotsOfScheduledTask,
      createTimeSlotForSchedueldTask(scheduledTask),
    ];
  }

  timeSlotsOfScheduledTask = timeSlotsOfScheduledTask.sort(
    (a, b) => a.startTimestamp - b.startTimestamp
  );

  fillTravelTimesAndPauseDurations(
    timeSlotsOfScheduledTask,
    earliestLunchOnDayOfScheduledTask.valueOf()
  );

  const workingHoursPart = calculateWorkingHoursPart(
    timeSlotsOfScheduledTask,
    workingHoursSettings.defaultWorkingHoursPerDay
  );

  let gapNowInMin = calculateTimeGapPart(timeSlotsOfScheduledTask);
  let gapBeforeChangesInMin = 0;

  let travelTimeNowInMin =
    calculateTravelTimeSumInMs(timeSlotsOfScheduledTask) / 60000;

  let travelTimeBeforeChangesInMin = 0;

  const scheduledTaskBeforeChanges =
    SchedulingModule.taskStateBeforeEdit?.scheduledTask;

  if (scheduledTaskBeforeChanges) {
    const currentUserId = scheduledTask.userId;
    const currentDayId = getDayId(scheduledTask.startDate);

    const previousUserId = scheduledTaskBeforeChanges.userId;
    const previousDayId = getDayId(scheduledTaskBeforeChanges.startDate);

    const didChangeUserOrDay =
      currentUserId !== previousUserId || currentDayId !== previousDayId;

    if (didChangeUserOrDay) {
      //The user or the date for the scheduled task has changed
      const earliestLunchOnDayOfTaskBeforeChanges = getEarliestLunchBreakForDate(
        scheduledTaskBeforeChanges.startDate,
        workingHoursSettings
      );

      const timeSlotsOfTaskBeforeChanges = [
        ...tasksOfAllUsersLookup
          .getAllTimeSlotsForSameUserAndDay(scheduledTaskBeforeChanges)
          .filter((t) => t.scheduledTask.id !== taskId)
          .map((t) => {
            return { ...t };
          }),
        createTimeSlotForSchedueldTask(scheduledTaskBeforeChanges),
      ].sort((a, b) => a.startTimestamp - b.startTimestamp);

      fillTravelTimesAndPauseDurations(
        timeSlotsOfTaskBeforeChanges,
        earliestLunchOnDayOfTaskBeforeChanges.valueOf()
      );

      travelTimeBeforeChangesInMin =
        calculateTravelTimeSumInMs(timeSlotsOfTaskBeforeChanges) / 60000;

      gapBeforeChangesInMin = calculateTimeGapPart(
        timeSlotsOfTaskBeforeChanges
      );

      const timeSlotsWithoutScheduledTask = timeSlotsOfScheduledTask.filter(
        (t) => t.scheduledTask.id !== scheduledTask.id
      );

      fillTravelTimesAndPauseDurations(
        timeSlotsWithoutScheduledTask,
        earliestLunchOnDayOfScheduledTask.valueOf()
      );

      travelTimeBeforeChangesInMin +=
        calculateTravelTimeSumInMs(timeSlotsWithoutScheduledTask) / 60000;

      gapBeforeChangesInMin += calculateTimeGapPart(
        timeSlotsWithoutScheduledTask
      );

      const timeSlotsOfTaskBeforeChangesWithoutScheduledTask = timeSlotsOfTaskBeforeChanges.filter(
        (t) => t.scheduledTask.id !== taskId
      );

      fillTravelTimesAndPauseDurations(
        timeSlotsOfTaskBeforeChangesWithoutScheduledTask,
        earliestLunchOnDayOfTaskBeforeChanges.valueOf()
      );

      travelTimeNowInMin +=
        calculateTravelTimeSumInMs(
          timeSlotsOfTaskBeforeChangesWithoutScheduledTask
        ) / 60000;

      gapNowInMin += calculateTimeGapPart(
        timeSlotsOfTaskBeforeChangesWithoutScheduledTask
      );
    } else {
      //The task was scheduled on the same day and for the same user as the previous state
      const timeSlotsBeforeChanges = timeSlotsOfScheduledTask
        .map((timeSlotWithScheduledTask) => {
          if (timeSlotWithScheduledTask.scheduledTask.id == scheduledTask.id) {
            return createTimeSlotForSchedueldTask(scheduledTaskBeforeChanges);
          } else {
            return { ...timeSlotWithScheduledTask };
          }
        })
        .sort((a, b) => a.startTimestamp - b.startTimestamp);

      fillTravelTimesAndPauseDurations(
        timeSlotsBeforeChanges,
        earliestLunchOnDayOfScheduledTask.valueOf()
      );

      travelTimeBeforeChangesInMin =
        calculateTravelTimeSumInMs(timeSlotsBeforeChanges) / 60000;

      gapBeforeChangesInMin += calculateTimeGapPart(timeSlotsBeforeChanges);
    }
  } else {
    //The task was not scheduled before
    const timeSlotsWithoutScheduledTask = timeSlotsOfScheduledTask.filter(
      (t) => t.scheduledTask.id !== scheduledTask.id
    );

    fillTravelTimesAndPauseDurations(
      timeSlotsWithoutScheduledTask,
      earliestLunchOnDayOfScheduledTask.valueOf()
    );

    travelTimeBeforeChangesInMin =
      (2 * getTravelTimeToDepotInMs() +
        calculateTravelTimeSumInMs(timeSlotsWithoutScheduledTask)) /
      60000;

    gapBeforeChangesInMin += calculateTimeGapPart(
      timeSlotsWithoutScheduledTask
    );
  }

  const travelTimePartInMin = travelTimeBeforeChangesInMin - travelTimeNowInMin;
  const timeGapPartInMin = gapNowInMin - gapBeforeChangesInMin;

  return (
    calculateObjectiveFunctionForDay(
      scheduledTask,
      planningControlSettings,
      travelTimePartInMin,
      timeGapPartInMin,
      workingHoursPart
    ) ?? undefined
  );
}
